/**
 * Product image viewer: click-to-magnify inside the zoom dialog.
 *
 * The theme's <zoom-dialog> opens, closes and moves between images. This module adds, from tablet width up:
 *   - click on the image (or the magnifier button): magnify by ZOOM_FACTOR at the pointer; click again: back to fit
 *   - while magnified, the visible part follows the pointer
 *   - click on the dark area around the frame: close
 * State lives on the frame (<drag-zoom-wrapper>), never on the <img>: the theme swaps the <img> for its
 * full-resolution copy after opening, which would drop anything stored on it.
 * Phone widths keep the theme's behaviour (tap closes, pinch zooms).
 */

const ZOOM_FACTOR = 2.5;
const desktop = window.matchMedia('(min-width: 750px)');
const FRAME = '.dialog-zoomed-gallery .product-media__drag-zoom-wrapper';

/**
 * @param {EventTarget | null} target
 * @param {string} selector
 * @returns {HTMLElement | null}
 */
function closest(target, selector) {
  return target instanceof Element ? /** @type {HTMLElement | null} */ (target.closest(selector)) : null;
}

/**
 * Points the magnified image at the pointer position.
 * @param {HTMLElement} frame
 * @param {number} clientX
 * @param {number} clientY
 */
function follow(frame, clientX, clientY) {
  const rect = frame.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
  const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
  frame.style.setProperty('--star-zoom-x', `${x.toFixed(2)}%`);
  frame.style.setProperty('--star-zoom-y', `${y.toFixed(2)}%`);
}

/** @param {HTMLElement} frame */
function zoomOut(frame) {
  frame.removeAttribute('data-star-zoomed');
  frame.style.removeProperty('--star-zoom');
  frame.querySelector('.star-zoom-toggle')?.setAttribute('aria-pressed', 'false');
}

/**
 * @param {HTMLElement} frame
 * @param {MouseEvent} event
 * @param {boolean} fromButton - The button magnifies the centre: its own corner is not what the viewer wants to see.
 */
function toggleZoom(frame, event, fromButton) {
  if (frame.hasAttribute('data-star-zoomed')) {
    zoomOut(frame);
    return;
  }

  if (fromButton || (event.clientX === 0 && event.clientY === 0)) {
    frame.style.setProperty('--star-zoom-x', '50%');
    frame.style.setProperty('--star-zoom-y', '50%');
  } else {
    follow(frame, event.clientX, event.clientY);
  }
  frame.style.setProperty('--star-zoom', String(ZOOM_FACTOR));
  frame.setAttribute('data-star-zoomed', '');
  frame.querySelector('.star-zoom-toggle')?.setAttribute('aria-pressed', 'true');
}

function zoomOutAll() {
  document.querySelectorAll(`${FRAME}[data-star-zoomed]`).forEach((frame) => {
    if (frame instanceof HTMLElement) zoomOut(frame);
  });
}

document.addEventListener('click', (event) => {
  const item = closest(event.target, '.dialog-zoomed-gallery [data-star-zoom-item]');
  if (!item) return;

  const frame = closest(event.target, FRAME);
  if (desktop.matches && frame) {
    toggleZoom(frame, event, Boolean(closest(event.target, '.star-zoom-toggle')));
    return;
  }

  const zoomDialog = /** @type {(HTMLElement & { close?: () => void }) | null} */ (item.closest('zoom-dialog'));
  zoomDialog?.close?.();
});

document.addEventListener('pointermove', (event) => {
  const frame = closest(event.target, `${FRAME}[data-star-zoomed]`);
  if (frame) follow(frame, event.clientX, event.clientY);
});

// Leaving an image (scrolling to the next one, closing, resizing below tablet width) returns it to fit.
// "scroll" and "close" do not bubble, so they are caught on the way down.
document.addEventListener(
  'scroll',
  (event) => {
    if (closest(event.target, '.dialog-zoomed-gallery__dialog')) zoomOutAll();
  },
  true
);
document.addEventListener(
  'close',
  (event) => {
    if (closest(event.target, '.dialog-zoomed-gallery__dialog')) zoomOutAll();
  },
  true
);
desktop.addEventListener('change', zoomOutAll);
